import { useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

import TextInput from '@/components/UI/OldFormBuilder/TextInput'
import Dropdown from '@/components/UI/OldFormBuilder/Dropdown'
import CopyTooltip from '@/components/UI/Tooltip/CopyTooltip'
import { FormData } from '../LmsIntegration'
type LtiSetup = {
  integrationType: string
  closeModal: () => void
  integrateSpecificAgent: boolean
  setTntegrateSpecificAgent: (value: boolean) => void
  data: LtiSetupData
  handleIntegration: () => Promise<void>
  formData: FormData
  setFormData: React.Dispatch<React.SetStateAction<FormData>>
  allAgents: any[]
  handleInputChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    name: string,
  ) => void
}

type LtiSetupData = {
  redirect_uri: string
  target_link_uri: string
  openid_connect_initiation_url: string
}

const MoodleLtiModal = (props: LtiSetup) => {
  const { t } = useTranslation('translation')
  const [currentStep, setCurrentStep] = useState<'configure' | 'enable'>(
    'configure',
  )

  const environment = (window as any).ENVIRONMENT
  const shouldAppendEnv = environment && environment !== 'production'
  const envSuffix = shouldAppendEnv ? ` [${environment}]` : ''
  const toolName = `Walter AI${envSuffix}`

  const renderConfigureStep = () => {
    return (
      <>
        <div className="tw-flex tw-gap-4 canvas-manual-integration">
          <div className="tw-px-4 w-90 left-content-wrap">
            <h2 className="mb-2">
              {t('Step 1: Set up external tool in Moodle')}
            </h2>
            <p className="mb-0 subhEad">
              {t(
                'To set up Walter AI as an external tool in Moodle, please do the following',
              )}
              :
            </p>
            <ol>
              <li>{t('Log in to Moodle as an admin')}.</li>
              <li>
                {t('Click on')} <strong>Site administration</strong>{' '}
                {t('link on navigation')}.
              </li>
              <li>
                {t('Site administration page, choose tab')}{' '}
                <strong>Plugins</strong>, {t('then click on')}{' '}
                <strong>External tool &gt; Manage tools</strong>.
              </li>
              <li>
                {t('Within the Add tool box, click on the link')}{' '}
                <strong>configure a tool manually</strong>.
              </li>
              <li>
                {t('Fill in the fields to configure the external tool')}
                <p className="mb-0 subhEad">
                  {t('Under section')} <strong>Tool settings</strong>:
                </p>
                <ul>
                  <li>
                    <span className="label">Tool name: </span>
                    <CopyTooltip text={toolName} showText={true} />
                  </li>
                  <li>
                    <span className="label">Tool URL: </span>
                    <CopyTooltip
                      text={props?.data?.target_link_uri}
                      showText={true}
                    />
                  </li>
                  <li>
                    <span className="label">Tool description: </span>
                    <CopyTooltip
                      text={`Noodle Factory - Walter AI tools${envSuffix}`}
                      showText={true}
                    />
                  </li>
                  <li>
                    <span className="label">LTI version: </span>
                    <em>LTI 1.3</em>
                  </li>
                  <li>
                    <span className="label">Public key type: </span>
                    <em>Keyset URL</em>
                  </li>
                  <li>
                    <span className="label">Public keyset: </span>
                    <CopyTooltip
                      text="https://oneauth.noodlefactory.ai/auth/.well-known/jwks.json"
                      showText={true}
                    />
                  </li>
                  <li>
                    <span className="label">Initiate login URL: </span>
                    <CopyTooltip
                      text={props?.data?.openid_connect_initiation_url}
                      showText={true}
                    />
                  </li>
                  <li>
                    <span className="label">Redirection URI(s): </span>
                    <CopyTooltip
                      text={props?.data?.redirect_uri}
                      showText={true}
                    />
                  </li>
                  <li>
                    <span className="label">Custom parameters: </span>
                    {t('Leave it empty')}
                  </li>
                  <li>
                    <span className="label">Tool configuration usage: </span>
                    <em>
                      Show in activity chooser and as a preconfigured tool
                    </em>
                  </li>
                  <li>
                    <span className="label">Default launch container: </span>
                    <em>Embed, without blocks</em>
                  </li>
                  <li>
                    <span className="label">{t('Tick on')}: </span>
                    <em>Supports Deep Linking (Content-Item Message)</em>
                  </li>
                  <li>
                    <span className="label">Content Selection URL: </span>
                    <CopyTooltip
                      text={`${props?.data?.target_link_uri}?target_app=activity_chooser`}
                      showText={true}
                    />
                  </li>
                </ul>

                <p className="mb-0 subhEad">
                  {t('Under section')} <strong>Privacy</strong>,{' '}
                  {t('select the following options')}:
                </p>
                <ul>
                  <li>
                    <span className="label">
                      Share launcher’s name with tool:
                    </span>{' '}
                    <em>Always</em>
                  </li>
                  <li>
                    <span className="label">
                      Share launcher’s email with tool:
                    </span>{' '}
                    <em>Always</em>
                  </li>
                </ul>
              </li>

              <li>
                {t('Click on')} <strong>Save changes</strong>.
              </li>
              <li>
                {t('Go back to')} <strong>Manage tools</strong>,{' '}
                {t(
                  'find the newly added tool and click on View configuration details icon, then fill in the form on the right the following details:',
                )}
                .
                <ul>
                  <li>
                    <em>Moodle URL</em>:{' '}
                    {t('This is the URL of your Moodle LMS instance')}
                  </li>
                  <li>
                    <em>Client ID</em>:{' '}
                    {t(
                      'Get the value from Tool configuration details popup in Moodle',
                    )}
                  </li>
                  <li>
                    <em>{t('Deployment ID')}</em>:{' '}
                    {t(
                      'Get the value from Tool configuration details popup in Moodle',
                    )}
                  </li>
                </ul>
              </li>
            </ol>
          </div>

          <div className="whit-box tw-px-4 right-content-wrap w-10">
            <div>
              <h2 className="mb-2">
                {t('Required fields for Noodle Factory')}
              </h2>
              <p className="mb-0">
                {t(
                  'As you complete each step, please fill up following values to set up the integration on Noodle Factory',
                )}
              </p>
              <form>
                <div className="flex-one mt-2">
                  <h3 className="mb-2">{t('Moodle URL')}</h3>
                  <TextInput
                    inputType="text"
                    placeholder={t('Moodle URL')}
                    autoFocus={false}
                    id="client-id-1"
                    value={props.formData?.LMS_URL}
                    inputChange={(e, name) => props.handleInputChange(e, name)}
                    max={undefined}
                    min={undefined}
                    tabIndex={undefined}
                    label=""
                    description=""
                    inputClass=""
                    name="LMS_URL"
                    disabled={false}
                    onKeyPress={() => {}}
                    useTextArea={false}
                    errorMessage=""
                    ref={undefined}
                    onPaste={undefined}
                    onFocus={undefined}
                    onBlur={undefined}
                    onKeyUp={undefined}
                    spellCheck={undefined}
                    required={undefined}
                    showError={undefined}
                  />
                </div>
                <div className="flex-one mt-2">
                  <h3 className="mb-2">{t('Client ID')}</h3>
                  <TextInput
                    inputType="text"
                    placeholder={t('Client ID')}
                    autoFocus={false}
                    id="client-id-2"
                    value={props.formData?.Client_ID}
                    inputChange={(e, name) => props.handleInputChange(e, name)}
                    max={undefined}
                    min={undefined}
                    tabIndex={undefined}
                    label=""
                    description=""
                    inputClass=""
                    name="Client_ID"
                    disabled={false}
                    onKeyPress={() => {}}
                    useTextArea={false}
                    errorMessage=""
                    ref={undefined}
                    onPaste={undefined}
                    onFocus={undefined}
                    onBlur={undefined}
                    onKeyUp={undefined}
                    spellCheck={undefined}
                    required={undefined}
                    showError={undefined}
                  />
                </div>
                <div className="flex-one mt-2">
                  <h3 className="mb-2">{t('Deployment ID')}</h3>
                  <TextInput
                    inputType="text"
                    placeholder={t('Deployment ID')}
                    autoFocus={false}
                    id="client-id-3"
                    value={props.formData?.Deployment_ID}
                    inputChange={(e, name) => props.handleInputChange(e, name)}
                    max={undefined}
                    min={undefined}
                    tabIndex={undefined}
                    label=""
                    description=""
                    inputClass=""
                    name="Deployment_ID"
                    disabled={false}
                    onKeyPress={() => {}}
                    useTextArea={false}
                    errorMessage=""
                    ref={undefined}
                    onPaste={undefined}
                    onFocus={undefined}
                    onBlur={undefined}
                    onKeyUp={undefined}
                    spellCheck={undefined}
                    required={undefined}
                    showError={undefined}
                  />
                </div>
              </form>
            </div>
            <div className="fOrm-box">
              <div className="tOggle-box">
                <div className="text mt-2">
                  <h3>{t('Integrate to Specific Agent')}</h3>
                </div>
                <Button className="icon-button">
                  <Form.Check // prettier-ignore
                    className="custom-toggle font-weight-bold font-14 text-nowrap mt-1"
                    type="switch"
                    id="toggle-skip-summary"
                    // label="Skip Summary Review"
                    defaultChecked={props.integrateSpecificAgent}
                    onChange={() =>
                      props.setTntegrateSpecificAgent(
                        !props.integrateSpecificAgent,
                      )
                    }
                  />
                </Button>
              </div>
              {props.integrateSpecificAgent && (
                <div className="flex-one mt-2">
                  <Dropdown
                    placeHolder={`${t('Select Agent')}`}
                    label=""
                    defaultValue={undefined}
                    description=""
                    dropdownClassNamePrefix=""
                    isClearable={false}
                    isSearchable={false}
                    data={props.allAgents}
                    dropdownContainerClassName=""
                    onChange={(data: { value: string }) =>
                      props.setFormData({
                        ...props.formData,
                        Agent_ID: data?.value,
                      })
                    }
                    isMandatory={false}
                    value={undefined}
                    isDisabled={false}
                    menuPosition="bottom"
                    isMulti={undefined}
                    customStyles={undefined}
                    isNested={undefined}
                    onInputChange={() => {}}
                    isCreatable={undefined}
                    onCreateOption={undefined}
                    menuIsOpen={undefined}
                  />
                </div>
              )}
              <p>
                {t(
                  'Only enable this if you are using a single agent for all courses',
                )}
              </p>
            </div>
          </div>
        </div>
        {/* Button Actions */}
        <Divider />
        <div className="tw-flex tw-justify-end tw-gap-2">
          <Button variant="outlined" onClick={props.closeModal}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={
              !props.formData.LMS_URL ||
              !props.formData.Client_ID ||
              !props.formData.Deployment_ID
            }
            onClick={() => {
              props.handleIntegration()
              setCurrentStep('enable')
            }}
          >
            {t('Next Step')}
          </Button>
        </div>
      </>
    )
  }

  const renderEnableStep = () => {
    return (
      <>
        <div className="tw-flex tw-gap-4 canvas-manual-integration">
          <div className="tw-px-4 tw-py-4 w-100">
            <h2 className="mb-2">
              {t('Step 2: Add Walter AI activities to courses')}
            </h2>
            <p className="mb-0 subhEad">
              {t(
                'After previous steps, Walter AI is enabled as an external tool in Moodle. Teachers can add Walter AI activities to courses with the following steps',
              )}
              :
            </p>
            <ol>
              <li>{t('Log in Moodle as a teacher.')}</li>
              <li>{t('Go to a course and turn on editing mode.')}</li>
              <li>
                {t('Add an activity to where you want to place the tool.')}
              </li>
              <li>
                {t('In the Add an activity or resource popup, choose')}{' '}
                <em>{toolName}</em>.
              </li>
              <li>
                {t('In the')} <em>New External tool</em>{' '}
                {t('form, click on the')} <strong>Select Content</strong>{' '}
                {t('button.')}{' '}
                {t(
                  'This will open the activity chooser where you can select from the following options',
                )}
                :
                <ul>
                  <li>
                    <em>Walter AI Admin{envSuffix}</em>:{' '}
                    {t(
                      'For teachers to access the Admin Portal to manage knowledge and activities.',
                    )}
                  </li>
                  <li>
                    <em>Walter AI Tutor{envSuffix}</em>:{' '}
                    {t(
                      'For students to access the Walter AI webchat for tutoring and assistance.',
                    )}
                  </li>
                  <li>
                    {t('Specific quiz or role play')}:{' '}
                    {t(
                      'For students to directly launch into a selected quiz or role play activity.',
                    )}
                  </li>
                </ul>
              </li>
              <li>
                {t(
                  'After selecting an activity, the form will be populated with the selected content.',
                )}{' '}
                {t('You can modify the activity name if needed.')}
              </li>
              <li>
                {t('Click on')} <strong>Save and return to course</strong>.
              </li>
              <li>
                {t(
                  'The selected Walter AI activity will now be available in the course for the intended audience to access.',
                )}
              </li>
            </ol>
          </div>
        </div>

        {/* Button Actions */}
        <Divider />
        <div className="tw-flex tw-justify-end tw-gap-2">
          <Button variant="contained" onClick={props.closeModal}>
            {t('Complete')}
          </Button>
        </div>
      </>
    )
  }

  if (currentStep === 'configure') {
    return renderConfigureStep()
  }

  return renderEnableStep()
}
export default MoodleLtiModal
